import { useEffect, useState } from "react";
import { createIntakeDeal, saveIntakeNote } from "../../api";
import type { CreatePayload } from "../../types/intake";
import { IntakeCapture } from "./IntakeCapture";

interface Props {
  onClose: () => void;
  /** Navigate into the new deal's focus view on success. */
  onCreated: (dealId: string) => void;
  onToast: (msg: string) => void;
}

/**
 * New Intake (Surface A) — cockpit CREATE. Thin host around the shared
 * IntakeCapture core: owns the modal shell, the dismiss guardrail, and the
 * loud note-failure retry. The mode grid + capture panels + Review live in
 * IntakeCapture (reused by Surface B's Enrich tab).
 *
 * Dismiss guardrail (build spec §4): backdrop never closes; X/Cancel only. An
 * untouched grid closes freely; once anything's typed (onDirty), X/Cancel asks
 * to confirm — the discard confirm renders as an OVERLAY so IntakeCapture stays
 * mounted and in-progress input is never wiped.
 */
export function NewIntakeModal({ onClose, onCreated, onToast }: Props) {
  const [touched, setTouched] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [noteFailed, setNoteFailed] = useState<{ dealId: string; content: string } | null>(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touched, noteFailed]);

  function requestClose() {
    if (noteFailed) return; // its own continue/retry flow owns the close
    if (touched) setConfirmDiscard(true);
    else onClose(); // untouched (or mode entered with no input) → free close
  }

  async function handleCreate(payload: CreatePayload) {
    // Throws (API error) propagate to IntakeReview, which shows them inline.
    const res = await createIntakeDeal(payload);
    if (res.note_saved === false) {
      // Deal created but note failed — do NOT navigate; surface the retry.
      setNoteFailed({ dealId: res.deal_id, content: payload.note });
      return;
    }
    if (payload.contact && !res.contact_linked) {
      onToast(`✓ Deal created — ${res.deal_name} · ⚠ borrower not linked`);
    } else {
      onToast(`✓ Deal created — ${res.deal_name}`);
    }
    onCreated(res.deal_id);
  }

  async function retryNote() {
    if (!noteFailed) return;
    setRetrying(true);
    try {
      await saveIntakeNote({ deal_id: noteFailed.dealId, kind: "intake", content: noteFailed.content });
      onToast("✓ Intake note saved");
      onCreated(noteFailed.dealId);
    } catch {
      onToast("⚠ Note retry failed — saved deal, note still missing");
      setRetrying(false);
    }
  }

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="intake-modal-card">
        <div className="intake-modal-head">
          <span className="intake-modal-title">NEW INTAKE</span>
          <button className="intake-modal-x" onClick={requestClose} title="Close" aria-label="Close">
            ✕
          </button>
        </div>

        {noteFailed ? (
          <div className="intake-note-failed">
            <div className="intake-note-failed-msg">
              ⚠ Deal created, but the intake note didn't save. The note is what
              Watchtower reads for intake — retry so this deal isn't mistaken for
              complete.
            </div>
            <div className="intake-actions">
              <button
                className="write-confirm-cancel"
                onClick={() => onCreated(noteFailed.dealId)}
                disabled={retrying}
              >
                CONTINUE ANYWAY
              </button>
              <button className="write-confirm-confirm" onClick={() => void retryNote()} disabled={retrying}>
                {retrying ? "RETRYING…" : "RETRY NOTE"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <IntakeCapture
              variant="create"
              onCreate={handleCreate}
              onCancel={requestClose}
              onDirty={() => setTouched(true)}
            />

            {/* Discard confirm — OVERLAY over the still-mounted IntakeCapture. */}
            {confirmDiscard && (
              <div className="intake-discard-overlay">
                <div className="intake-discard">
                  <div className="intake-discard-msg">Discard this intake? Your progress will be lost.</div>
                  <div className="intake-actions">
                    <button className="write-confirm-cancel" onClick={() => setConfirmDiscard(false)}>
                      KEEP EDITING
                    </button>
                    <button className="write-confirm-confirm" onClick={onClose}>
                      DISCARD
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
